import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { getSection, getAllStyles, getAllPromptsAdmin, getAllTags } from '../graphql/queries'
import { updateSection, createContent, deleteSection } from '../graphql/mutation'
import { uploadToS3 } from '../utils/uploadToS3'
import ImageUploadField from '../components/imageUpload'
import { getMutationOptions } from '../utils/apolloHelpers'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { designType } from '../utils/enum'



export default function SectionPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [section, setSection] = useState({
    name: '',
    banner: '',
    secondBanner: '',
    gifUrl: '',
    webmUrl: '',
    webpUrl: '',
    videoUrl: '',
    designType: '',
    rowNumber: '',
    isPublic: false,
    styleIds: [],
    promptIds: [],
    tagIds: [],
    highlighted: false,
  })

  const [selectedStyles, setSelectedStyles] = useState([])
  const [selectedPrompts, setSelectedPrompts] = useState([])
  const [selectedTags, setSelectedTags] = useState([])
  const [styleSearchTerm, setStyleSearchTerm] = useState('')
  const [promptSearchTerm, setPromptSearchTerm] = useState('')
  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  // Modal states for custom content
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedContent, setSelectedContent] = useState(null)
  const [assetChoice, setAssetChoice] = useState('default') // 'default' or 'custom'
  const [customAssets, setCustomAssets] = useState({
    banner: '',
    secondBanner: '',
    videoUrl: '',
    gifUrl: '',
    webmUrl: '',
    webpUrl: '',
    detailImage: '',
    designType: 'MEDIUM',
    rowNumber: 1
  })
  const [selectedFiles, setSelectedFiles] = useState({
    banner: null,
    video: null,
    gif: null,
    webm: null,
    webp: null,
    secondBanner: null,
    detailImage: null
  })

  // Media file states
  const [bannerFile, setBannerFile] = useState(null)
  const [selectedBannerUrl, setSelectedBannerUrl] = useState(null)
  const [secondBannerFile, setSecondBannerFile] = useState(null)
  const [selectedSecondBannerUrl, setSelectedSecondBannerUrl] = useState(null)
  const [videoFile, setVideoFile] = useState(null)
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null)
  const [gifFile, setGifFile] = useState(null)
  const [selectedGifUrl, setSelectedGifUrl] = useState(null)
  const [webmFile, setWebmFile] = useState(null)
  const [selectedWebmUrl, setSelectedWebmUrl] = useState(null)
  const [webpFile, setWebpFile] = useState(null)
  const [selectedWebpUrl, setSelectedWebpUrl] = useState(null)

  // Fetch section data
  const { loading: sectionLoading, error: sectionError, data: sectionData } = useQuery(getSection, {
    variables: { sectionId: id }
  })

  // Fetch styles and prompts for selection
  const { data: stylesData, loading: stylesLoading } = useQuery(getAllStyles, {
    variables: { page: 1, pageSize: 1000 }
  })

  const { data: promptsData, loading: promptsLoading } = useQuery(getAllPromptsAdmin, {
    variables: { page: 1, pageSize: 1000 }
  })

  const { data: tagsData } = useQuery(getAllTags)

  const styles = stylesData?.getAllStylesAdmin?.styles || []
  const prompts = promptsData?.getAllPromptsAdmin?.prompts || []
  const tags = tagsData?.getAllTags || []

  // Filter styles and prompts based on search terms
  const filteredStyles = styles.filter(style =>
    style.name.toLowerCase().includes(styleSearchTerm.toLowerCase()) ||
    style.designType.toLowerCase().includes(styleSearchTerm.toLowerCase())
  ).sort((a, b) => {
    // Show selected items first
    const aSelected = selectedStyles.some(s => s.id === a.id)
    const bSelected = selectedStyles.some(s => s.id === b.id)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  const filteredPrompts = prompts.filter(prompt =>
    prompt.prompt.toLowerCase().includes(promptSearchTerm.toLowerCase()) ||
    prompt.Style?.name.toLowerCase().includes(promptSearchTerm.toLowerCase()) ||
    prompt.gender?.toLowerCase().includes(promptSearchTerm.toLowerCase()) ||
    prompt.toolType?.toLowerCase().includes(promptSearchTerm.toLowerCase())
  ).sort((a, b) => {
    // Show selected items first
    const aSelected = selectedPrompts.some(p => p.id === a.id)
    const bSelected = selectedPrompts.some(p => p.id === b.id)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  // Initialize form with section data
  useEffect(() => {
    if (sectionData?.getSection) {
      const data = sectionData.getSection
      setSection({
        name: data.name,
        banner: data.banner,
        secondBanner: data.secondBanner,
        gifUrl: data.gifUrl,
        webmUrl: data.webmUrl,
        webpUrl: data.webpUrl,
        videoUrl: data.videoUrl,
        designType: data.designType,
        rowNumber: data.rowNumber,
        isPublic: data.isPublic,
        styleIds: data.styles?.map(style => style.id) || [],
        promptIds: data.tools?.map(tool => tool.id) || [],
        highlighted: data.highlighted || false,
        tagIds: data.tags?.map(tag => tag.id) || [],
      })
      
      // Set selected media URLs
      setSelectedBannerUrl(data.banner)
      setSelectedSecondBannerUrl(data.secondBanner)
      setSelectedVideoUrl(data.videoUrl)
      setSelectedGifUrl(data.gifUrl)
      setSelectedWebmUrl(data.webmUrl)
      setSelectedWebpUrl(data.webpUrl)
      
      setSelectedStyles(data.styles || [])
      setSelectedPrompts(data.tools || [])
      setSelectedTags(data.tags || [])
    }
  }, [sectionData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setSection((prev) => ({ ...prev, [name]: value }))
  }

  // Media file selection handlers
  const handleBannerSelected = (e) => {
    const selectedFile = e.target.files[0]
    setSelectedBannerUrl(URL.createObjectURL(selectedFile))
    setBannerFile(selectedFile)
  }

  const handleSecondBannerSelected = (e) => {
    const selectedFile = e.target.files[0]
    setSelectedSecondBannerUrl(URL.createObjectURL(selectedFile))
    setSecondBannerFile(selectedFile)
  }

  const handleVideoSelected = (e) => {
    const selectedFile = e.target.files[0]
    setSelectedVideoUrl(URL.createObjectURL(selectedFile))
    setVideoFile(selectedFile)
  }

  const handleGifSelected = (e) => {
    const selectedFile = e.target.files[0]
    setSelectedGifUrl(URL.createObjectURL(selectedFile))
    setGifFile(selectedFile)
  }

  const handleWebmSelected = (e) => {
    const selectedFile = e.target.files[0]
    setSelectedWebmUrl(URL.createObjectURL(selectedFile))
    setWebmFile(selectedFile)
  }

  const handleWebpSelected = (e) => {
    const selectedFile = e.target.files[0]
    setSelectedWebpUrl(URL.createObjectURL(selectedFile))
    setWebpFile(selectedFile)
  }

  const handleStyleCheckboxChange = (style, checked) => {
    if (checked) {
      setSelectedStyles(prev => [...prev, style])
      setSection(prev => ({
        ...prev,
        styleIds: [...prev.styleIds, style.id]
      }))
    } else {
      setSelectedStyles(prev => prev.filter(s => s.id !== style.id))
      setSection(prev => ({
        ...prev,
        styleIds: prev.styleIds.filter(id => id !== style.id)
      }))
    }
  }

  const handlePromptCheckboxChange = (prompt, checked) => {
    if (checked) {
      setSelectedPrompts(prev => [...prev, prompt])
      setSection(prev => ({
        ...prev,
        promptIds: [...prev.promptIds, prompt.id]
      }))
    } else {
      setSelectedPrompts(prev => prev.filter(p => p.id !== prompt.id))
      setSection(prev => ({
        ...prev,
        promptIds: prev.promptIds.filter(id => id !== prompt.id)
      }))
    }
  }

  const [updateSectionMutation] = useMutation(updateSection)
  const [createContentMutation] = useMutation(createContent)
  const [deleteSectionMutation] = useMutation(deleteSection)

  const handleUpdateSection = async () => {
    if (!section.name || !section.designType || !section.rowNumber) {
      setSnackBarMessage('Please fill in all required fields.')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      // Upload media files if they exist
      const bannerUrl = bannerFile ? await uploadToS3(bannerFile, 'img') : section.banner
      const secondBannerUrl = secondBannerFile ? await uploadToS3(secondBannerFile, 'img') : section.secondBanner
      const videoUrl = videoFile ? await uploadToS3(videoFile, 'video') : section.videoUrl
      const gifUrl = gifFile ? await uploadToS3(gifFile, 'gif') : section.gifUrl
      const webmUrl = webmFile ? await uploadToS3(webmFile, 'video') : section.webmUrl
      const webpUrl = webpFile ? await uploadToS3(webpFile, 'img') : section.webpUrl

      const response = await updateSectionMutation({
        variables: {
          sectionId: id,
          name: section.name,
          banner: bannerUrl,
          secondBanner: secondBannerUrl,
          gifUrl: gifUrl,
          webmUrl: webmUrl,
          webpUrl: webpUrl,
          videoUrl: videoUrl,
          designType: section.designType,
          rowNumber: Number(section.rowNumber),
          isPublic: section.isPublic,
          styleIds: section.styleIds,
          promptIds: section.promptIds,
          tagIds: section.tagIds,
          highlighted: section.highlighted,
        },
        // Properly refetch all related queries
        ...getMutationOptions('SECTIONS', id)
      })

      if (response.data) {
        setSnackBarMessage('Section updated successfully')
        setSnackBarOpen(true)
        setSnackBarSeverity('success')
        setTimeout(() => {
          navigate('/sections', { 
            state: { 
              refreshData: true,
              updatedItemId: id 
            }
          });
        }, 1000)
      } else {
        setSnackBarMessage('Update failed')
        setSnackBarOpen(true)
        setSnackBarSeverity('error')
      }
    } catch (error) {
      setSnackBarMessage(`Update failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleDeleteSection = async () => {
    setProgress(true)
    try {
      const response = await deleteSectionMutation({
        variables: { sectionId: id },
        ...getMutationOptions('SECTIONS')
      })

      if (response.data) {
        setSnackBarMessage('Section deleted successfully')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        setTimeout(() => {
          navigate('/sections', { 
            state: { 
              refreshData: true,
              deletedItemId: id 
            }
          });
        }, 1000)
      } else {
        setSnackBarMessage('Delete failed')
        setSnackBarSeverity('error')
        setSnackBarOpen(true)
      }
    } catch (error) {
      setSnackBarMessage(`Delete failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
      setDeleteConfirmOpen(false)
    }
  }

  // Modal handlers
  const handleAddToSection = (content, contentType) => {
    setSelectedContent({ ...content, contentType })
    setModalOpen(true)
    setAssetChoice('default')
    
    // Pre-populate custom assets fields with existing content assets
    const prefilledAssets = {
      banner: content.banner || '',
      secondBanner: content.secondBanner || '',
      videoUrl: content.videoUrl || '',
      gifUrl: content.gifUrl || '',
      webmUrl: content.webmUrl || '',
      webpUrl: content.webpUrl || '',
      detailImage: content.detailImage || '',
      designType: content.designType || 'MEDIUM',
      rowNumber: 1
    }
    
    setCustomAssets(prefilledAssets)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedContent(null)
    setAssetChoice('default')
    setCustomAssets({ banner: '', secondBanner: '', videoUrl: '', gifUrl: '', webmUrl: '', webpUrl: '', detailImage: '', designType: 'MEDIUM', rowNumber: 1 })
    setSelectedFiles({ banner: null, video: null, gif: null, webm: null, webp: null, secondBanner: null, detailImage: null })
  }

  const handleFileSelect = (type, event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [type]: file }))
    }
  }

  const handleAddWithDefault = async () => {
    if (!selectedContent) return

    try {
      // Just add to section directly (existing behavior)
      const isStyle = selectedContent.contentType === 'STYLE'
      if (isStyle) {
        handleStyleCheckboxChange(selectedContent, true)
      } else {
        handlePromptCheckboxChange(selectedContent, true)
      }
      
      setSnackBarMessage(`${selectedContent.contentType} added to section with default assets`)
      setSnackBarSeverity('success')
      setSnackBarOpen(true)
      handleCloseModal()
    } catch (error) {
      setSnackBarMessage('Failed to add content')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    }
  }

  const handleAddWithCustomAssets = async () => {
    if (!selectedContent) return

    setProgress(true)
    try {
      let finalBannerUrl = customAssets.banner
      let finalSecondBannerUrl = customAssets.secondBanner
      let finalVideoUrl = customAssets.videoUrl
      let finalGifUrl = customAssets.gifUrl
      let finalWebmUrl = customAssets.webmUrl
      let finalWebpUrl = customAssets.webpUrl
      let finalDetailImageUrl = customAssets.detailImage

      // Upload banner file if selected
      if (selectedFiles.banner) {
        setSnackBarMessage('Uploading banner image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalBannerUrl = await uploadToS3(selectedFiles.banner, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload banner: ${uploadError.message}`)
        }
      }

      // Upload second banner file if selected
      if (selectedFiles.secondBanner) {
        setSnackBarMessage('Uploading second banner image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalSecondBannerUrl = await uploadToS3(selectedFiles.secondBanner, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload second banner: ${uploadError.message}`)
        }
      }

      // Upload video file if selected
      if (selectedFiles.video) {
        setSnackBarMessage('Uploading video...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalVideoUrl = await uploadToS3(selectedFiles.video, 'video')
        } catch (uploadError) {
          throw new Error(`Failed to upload video: ${uploadError.message}`)
        }
      }

      // Upload gif file if selected
      if (selectedFiles.gif) {
        setSnackBarMessage('Uploading GIF...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalGifUrl = await uploadToS3(selectedFiles.gif, 'gif')
        } catch (uploadError) {
          throw new Error(`Failed to upload GIF: ${uploadError.message}`)
        }
      }

      // Upload webm file if selected
      if (selectedFiles.webm) {
        setSnackBarMessage('Uploading WEBM video...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalWebmUrl = await uploadToS3(selectedFiles.webm, 'video')
        } catch (uploadError) {
          throw new Error(`Failed to upload WEBM: ${uploadError.message}`)
        }
      }

      // Upload webp file if selected
      if (selectedFiles.webp) {
        setSnackBarMessage('Uploading WEBP image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalWebpUrl = await uploadToS3(selectedFiles.webp, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload WEBP: ${uploadError.message}`)
        }
      }

      // Upload detail image file if selected
      if (selectedFiles.detailImage) {
        setSnackBarMessage('Uploading detail image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalDetailImageUrl = await uploadToS3(selectedFiles.detailImage, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload detail image: ${uploadError.message}`)
        }
      }

      // First create custom content
      await createContentMutation({
        variables: {
          contentId: selectedContent.id, // Use the selected content's ID (prompt/style)
          contentType: selectedContent.contentType, // Use the actual content type (PROMPT/STYLE)
          designType: customAssets.designType,
          banner: finalBannerUrl || null,
          secondBanner: finalSecondBannerUrl || null,
          videoUrl: finalVideoUrl || null,
          gifUrl: finalGifUrl || null,
          webmUrl: finalWebmUrl || null,
          webpUrl: finalWebpUrl || null,
          detailImage: finalDetailImageUrl || null,
          rowNumber: customAssets.rowNumber,
          sectionId: id, // Link this content to the current section
          // tagId, styleId, promptId undefined - let backend use automatic logic
        }
      })

      // Then add to section
      const isStyle = selectedContent.contentType === 'STYLE'
      if (isStyle) {
        handleStyleCheckboxChange(selectedContent, true)
      } else {
        handlePromptCheckboxChange(selectedContent, true)
      }

      setSnackBarMessage(`${selectedContent.contentType} added to section with custom assets`)
      setSnackBarSeverity('success')
      setSnackBarOpen(true)
      handleCloseModal()
    } catch (error) {
      setSnackBarMessage('Failed to add custom content')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  if (sectionLoading) return <CircularProgress />
  if (sectionError) return <p>Error: {sectionError.message}</p>

  console.log("selectedPrompts", selectedPrompts);
  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
        message={snackBarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          onClose={handleSnackBarClose}
          severity={snackBarSeverity}
          sx={{ width: '100%' }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Sticky Header with Actions */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant='h4' sx={{ color: '#212529', fontWeight: 'bold' }}>
            Section Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: '#e74c3c',
                color: '#e74c3c',
                '&:hover': { 
                  borderColor: '#c0392b',
                  backgroundColor: '#fdf2f2'
                }
              }}
            >
              Delete Section
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateSection}
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                backgroundColor: '#3498db',
                '&:hover': { backgroundColor: '#2980b9' }
              }}
            >
              Update Section
            </Button>
          </Box>
        </Box>
      </Box>
      
      {progress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={80} thickness={5} />
        </Box>
      )}

      {/* Main Content Container */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        
        {/* Basic Information Card */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <Box sx={{ p: 4, pb: 2 }}>
            <Typography variant='h5' sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Basic Information
            </Typography>
          </Box>

          {/* Basic Fields Accordion */}
          <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                px: 4, 
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#e9ecef' }
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 600, color: '#495057' }}>
                Section Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 4, pb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {[
                    { name: 'name', label: 'Section Name', type: 'text' },
                    { name: 'rowNumber', label: 'Row Number', type: 'number' },
                  ].map(({ name, label, type }) => (
                    <TextField
                      fullWidth
                      key={name}
                      id={`${name}-input`}
                      label={label}
                      variant="outlined"
                      name={name}
                      type={type}
                      value={section[name]}
                      onChange={handleChange}
                      inputProps={{ ...(type === 'number' && { min: 0 }) }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ))}
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Design Type */}
                  <FormControl fullWidth>
                    <InputLabel>Design Type</InputLabel>
                    <Select 
                      name="designType" 
                      onChange={handleChange} 
                      value={section.designType}
                      label="Design Type"
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    >
                      {designType.map((i) => (
                        <MenuItem key={i.value} value={i.value}>
                          {i.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {/* Tags Selection */}
                  <Autocomplete
                    multiple
                    options={tags}
                    getOptionLabel={(option) => option.name}
                    value={selectedTags}
                    onChange={(event, newValue) => {
                      setSelectedTags(newValue)
                      setSection(prev => ({
                        ...prev,
                        tagIds: newValue.map(tag => tag.id)
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="Tags"
                        placeholder="Select tags..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.name}
                          size="small"
                          sx={{
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                            '&:hover': { backgroundColor: '#bbdefb' }
                          }}
                        />
                      ))
                    }
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Visibility Settings Accordion */}
        {/*  <Accordion defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                px: 4, 
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#e9ecef' }
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 600, color: '#495057' }}>
                Visibility Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 4, pb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {[
                  { key: 'isPublic', label: 'Is Public', checked: section.isPublic },
                  { key: 'highlighted', label: 'Highlighted', checked: section.highlighted }
                ].map(({ key, label, checked }) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 500, color: '#2c3e50' }}>{label}</Typography>
                    <Box
                      onClick={() => setSection(prev => ({ ...prev, [key]: !prev[key] }))}
                      sx={{
                        width: 50,
                        height: 24,
                        borderRadius: 12,
                        backgroundColor: checked ? '#4caf50' : '#ddd',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '2px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          transform: checked ? 'translateX(26px)' : 'translateX(0)',
                          transition: 'transform 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion> */}
        </Paper>

        {/* Media Assets Section */}
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Typography variant='h5' sx={{ mb: 4, fontWeight: 'bold', color: '#2c3e50' }}>
            Media Assets
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 4 }}>
            <Box>
              <ImageUploadField
                label="Banner"
                file={bannerFile}
                imageUrl={selectedBannerUrl ?? section?.banner}
                inputName="banner"
                onImageChange={handleBannerSelected}
                onDelete={() => {
                  setSelectedBannerUrl(null);
                  setBannerFile(null);
                  setSection(prev => ({ ...prev, banner: null }));
                }}
              />
            </Box>

            <Box>
              <ImageUploadField
                label="Second Banner"
                file={secondBannerFile}
                imageUrl={selectedSecondBannerUrl ?? section?.secondBanner}
                inputName="secondBanner"
                onImageChange={handleSecondBannerSelected}
                onDelete={() => {
                  setSelectedSecondBannerUrl(null);
                  setSecondBannerFile(null);
                  setSection(prev => ({ ...prev, secondBanner: null }));
                }}
              />
            </Box>

            <Box>
              <ImageUploadField
                label="Video"
                file={videoFile}
                imageUrl={selectedVideoUrl ?? section?.videoUrl}
                inputName="video"
                isVideo
                onImageChange={handleVideoSelected}
                onDelete={() => {
                  setSelectedVideoUrl(null);
                  setVideoFile(null);
                  setSection(prev => ({ ...prev, videoUrl: null }));
                }}
              />
            </Box>

            <Box>
              <ImageUploadField
                label="GIF"
                file={gifFile}
                imageUrl={selectedGifUrl ?? section?.gifUrl}
                inputName="gif"
                isVideo
                onImageChange={handleGifSelected}
                onDelete={() => {
                  setSelectedGifUrl(null);
                  setGifFile(null);
                  setSection(prev => ({ ...prev, gifUrl: null }));
                }}
              />
            </Box>
          </Box>
          
          {/* Additional Media Assets Row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4, mt: 4 }}>
            <Box>
              <ImageUploadField
                label="WEBM Video"
                file={webmFile}
                imageUrl={selectedWebmUrl ?? section?.webmUrl}
                inputName="webm"
                isVideo
                onImageChange={handleWebmSelected}
                onDelete={() => {
                  setSelectedWebmUrl(null);
                  setWebmFile(null);
                  setSection(prev => ({ ...prev, webmUrl: null }));
                }}
              />
            </Box>

            <Box>
              <ImageUploadField
                label="WEBP Image"
                file={webpFile}
                imageUrl={selectedWebpUrl ?? section?.webpUrl}
                inputName="webp"
                onImageChange={handleWebpSelected}
                onDelete={() => {
                  setSelectedWebpUrl(null);
                  setWebpFile(null);
                  setSection(prev => ({ ...prev, webpUrl: null }));
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Selected Items Display */}
        {(selectedStyles.length > 0 || selectedPrompts.length > 0) && (
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
            <Typography variant='h5' sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50' }}>
              Selected Items
            </Typography>
            {selectedStyles.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
                  Selected Styles ({selectedStyles.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedStyles.map((style) => (
                    <Chip
                      key={style.id}
                      label={style.name}
                      onDelete={() => handleStyleCheckboxChange(style, false)}
                      variant="outlined"
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        borderColor: '#81c784',
                        '&:hover': { backgroundColor: '#c8e6c9' }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            {selectedPrompts.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#495057' }}>
                  Selected Prompts ({selectedPrompts.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedPrompts.map((prompt) => {
                    // Create a display label with 10-20 characters
                    let displayLabel = ''
                    if (prompt.name) {
                      displayLabel = prompt.name.length > 20 ? prompt.name.substring(0, 20) + '...' : prompt.name
                    } else if (prompt.prompt) {
                      displayLabel = prompt.prompt.length > 20 ? prompt.prompt.substring(0, 20) + '...' : prompt.prompt
                    } else {
                      displayLabel = 'Untitled Prompt'
                    }
                    
                    return (
                      <Chip
                        key={prompt.id}
                        label={displayLabel}
                        onDelete={() => handlePromptCheckboxChange(prompt, false)}
                        variant="outlined"
                        size="small"
                        title={prompt.prompt || prompt.name} // Show full text on hover
                        sx={{
                          backgroundColor: '#fff3e0',
                          color: '#e65100',
                          borderColor: '#ffb74d',
                          '&:hover': { backgroundColor: '#ffe0b2' }
                        }}
                      />
                    )
                  })}
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Selection Tables Card */}
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Typography variant='h5' sx={{ mb: 4, fontWeight: 'bold', color: '#2c3e50' }}>
            Select Items
          </Typography>
          
          {/* Tables Container with Flex Layout */}
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Styles Table */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Select Styles
              </Typography>
              
              {/* Styles Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search styles by name or design type..."
                value={styleSearchTerm}
                onChange={(e) => setStyleSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Banner</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stylesLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : filteredStyles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No styles found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredStyles.map((style) => {
                        const isSelected = selectedStyles.some(s => s.id === style.id)
                        return (
                          <TableRow key={style.id} hover>
                            <TableCell>
                              {style.banner ? (
                                <img
                                  src={style.banner}
                                  alt="Style banner"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {style.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {style.designType}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isSelected ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleStyleCheckboxChange(style, false)}
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddToSection(style, 'STYLE')}
                                >
                                  Add to Section
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Prompts Table */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Select Prompts
              </Typography>
              
              {/* Prompts Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search prompts by text, style, gender, or tool type..."
                value={promptSearchTerm}
                onChange={(e) => setPromptSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Banner</TableCell>
                      <TableCell>Prompt</TableCell>
                      <TableCell>Style</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {promptsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : filteredPrompts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No prompts found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPrompts.map((prompt) => {
                        const isSelected = selectedPrompts.some(p => p.id === prompt.id)
                        return (
                          <TableRow key={prompt.id} hover>
                            <TableCell>
                              {prompt.banner ? (
                                <img
                                  src={prompt.banner}
                                  alt="Prompt banner"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    No Image
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200 }}>
                              <Typography variant="body2">
                                {prompt.prompt?.length > 60 ? prompt.prompt.substring(0, 60) + '...' : prompt.prompt}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {prompt.Style?.name || 'No Style'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {isSelected ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handlePromptCheckboxChange(prompt, false)}
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddToSection(prompt, 'PROMPT')}
                                >
                                  Add to Section
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Custom Content Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Add {selectedContent?.contentType} to Section
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedContent && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedContent.contentType}: {selectedContent.name || 'Untitled'}
                </Typography>
                {selectedContent.banner && (
                  <img
                    src={selectedContent.banner}
                    alt="Content preview"
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginTop: '8px'
                    }}
                  />
                )}
              </Box>
            )}

            <Typography variant="h6" gutterBottom>
              Choose Asset Option:
            </Typography>
            
            <RadioGroup
              value={assetChoice}
              onChange={(e) => setAssetChoice(e.target.value)}
            >
              <FormControlLabel
                value="default"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Use Default Assets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Use the original banner, video, and design type from the {selectedContent?.contentType?.toLowerCase()}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="custom"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Add Custom Assets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Override the default assets with custom banner, video, and design type for this section
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

            {assetChoice === 'custom' && (
              <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Custom Assets
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Banner Image:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="Banner URL"
                        value={customAssets.banner}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, banner: e.target.value }))}
                        placeholder="https://example.com/banner.jpg"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="banner-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('banner', e)}
                        />
                        <label htmlFor="banner-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                          >
                            Choose Image
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.banner && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.banner.name}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Second Banner:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="Second Banner URL"
                        value={customAssets.secondBanner}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, secondBanner: e.target.value }))}
                        placeholder="https://example.com/second-banner.jpg"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="second-banner-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('secondBanner', e)}
                        />
                        <label htmlFor="second-banner-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                          >
                            Choose Image
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.secondBanner && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.secondBanner.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Video:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="Video URL"
                        value={customAssets.videoUrl}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, videoUrl: e.target.value }))}
                        placeholder="https://example.com/video.mp4"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="video/*"
                          style={{ display: 'none' }}
                          id="video-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('video', e)}
                        />
                        <label htmlFor="video-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<VideoLibraryIcon />}
                            size="small"
                          >
                            Choose Video
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.video && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.video.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      GIF:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="GIF URL"
                        value={customAssets.gifUrl}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, gifUrl: e.target.value }))}
                        placeholder="https://example.com/animation.gif"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="image/gif"
                          style={{ display: 'none' }}
                          id="gif-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('gif', e)}
                        />
                        <label htmlFor="gif-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                          >
                            Choose GIF
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.gif && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.gif.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      WEBM Video:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="WEBM URL"
                        value={customAssets.webmUrl}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, webmUrl: e.target.value }))}
                        placeholder="https://example.com/video.webm"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="video/webm"
                          style={{ display: 'none' }}
                          id="webm-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('webm', e)}
                        />
                        <label htmlFor="webm-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<VideoLibraryIcon />}
                            size="small"
                          >
                            Choose WEBM
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.webm && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.webm.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      WEBP Image:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="WEBP URL"
                        value={customAssets.webpUrl}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, webpUrl: e.target.value }))}
                        placeholder="https://example.com/image.webp"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="image/webp"
                          style={{ display: 'none' }}
                          id="webp-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('webp', e)}
                        />
                        <label htmlFor="webp-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                          >
                            Choose WEBP
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.webp && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.webp.name}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Detail Image:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                      <TextField
                        label="Detail Image URL"
                        value={customAssets.detailImage}
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, detailImage: e.target.value }))}
                        placeholder="https://example.com/detail-image.jpg"
                        sx={{ flex: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mx: 1, mb: 1 }}>
                        OR
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="detail-image-upload-section"
                          type="file"
                          onChange={(e) => handleFileSelect('detailImage', e)}
                        />
                        <label htmlFor="detail-image-upload-section">
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                          >
                            Choose Image
                          </Button>
                        </label>
                      </Box>
                    </Box>
                    
                    {selectedFiles.detailImage && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Selected: {selectedFiles.detailImage.name}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Design Type</InputLabel>
                      <Select
                        value={customAssets.designType}
                        label="Design Type"
                        onChange={(e) => setCustomAssets(prev => ({ ...prev, designType: e.target.value }))}
                      >
                        {designType.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Row Number"
                      type="number"
                      value={customAssets.rowNumber}
                      onChange={(e) => setCustomAssets(prev => ({ ...prev, rowNumber: parseInt(e.target.value) || 1 }))}
                      placeholder="1"
                      fullWidth
                      helperText="Order position for this content item"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={assetChoice === 'default' ? handleAddWithDefault : handleAddWithCustomAssets}
            disabled={progress}
          >
            {progress ? <CircularProgress size={20} /> : `Add ${assetChoice === 'default' ? 'with Default' : 'with Custom'} Assets`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#e74c3c', fontWeight: 'bold' }}>
          Delete Section
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this section? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Section: <strong>{section.name}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ color: '#6c757d' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSection}
            disabled={progress}
            startIcon={progress ? <CircularProgress size={20} /> : <DeleteIcon />}
            sx={{
              backgroundColor: '#e74c3c',
              '&:hover': { backgroundColor: '#c0392b' }
            }}
          >
            {progress ? 'Deleting...' : 'Delete Section'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
} 